/**
 * ATTEST-CLASSIFY-UI-RENDER-1 — the rendered `/classify` decision.
 *
 * Split out of `ClassifyStep` because that file crossed the 400-line hard stop
 * (FILE_SIZE_POLICY) once the refusal path started rendering its detail. The
 * boundary is a real one, not a line-count dodge: everything here is a pure
 * function of the `RoutingDecision` the server returned, with no form state and
 * no callbacks. The intake questions stay in `ClassifyStep`.
 *
 * The decision renders on BOTH paths. Previously the whole panel was gated on
 * "routed", so a refusal showed the refusal banner and nothing else: the CPA was
 * told "no" without being shown which of their items caused it, what the rest of
 * the ask would have been, or what they could say instead — at the one moment
 * that detail matters most.
 */
import type { AttestPolicy, ExcludedItem, RoutingDecision } from '@/components/attest/api'

/**
 * Plain-English headline per `RefusalReason`. The enum value is still shown next
 * to it — a CPA quoting a refusal back to a lender needs the exact token.
 */
const REFUSAL_LABELS: Record<string, string> = {
  NO_REQUEST_ITEMS: 'Nothing was asked',
  NO_PERMISSIBLE_ASSERTIONS: 'Nothing in this ask can be said',
  ASSURANCE_LEVEL_UNSUPPORTED: 'This is an opinion engagement, not a letter',
  PROCEDURES_NOT_AGREED: 'The procedures have not been agreed',
  ISSUER_NOT_ATTEST_ELIGIBLE: 'The issuing firm is not attest-eligible',
  LETTER_TYPE_ENGAGEMENT_MISMATCH: 'The letter type does not fit this work',
  INTENT_CHECK_UNAVAILABLE: 'The compliance check could not run',
}

/**
 * One bucket of requested items, with the finding against each.
 *
 * Excluded and needs-review render through the same component because they are
 * the same shape, and deliberately in different colours because they are not the
 * same fact: red is "the deny list fired", amber is "we could not check".
 * Collapsing them would tell a CPA their wording was rejected when the model was
 * merely unreachable.
 */
function ItemBucket({
  testId, tone, heading, blurb, items, showAlternatives,
}: {
  testId: string
  tone: 'red' | 'amber'
  heading: string
  blurb?: string
  items: ExcludedItem[]
  showAlternatives: boolean
}) {
  if (items.length === 0) return null
  const head = tone === 'red' ? 'text-red-900/70' : 'text-amber-900/80'
  const card = tone === 'red' ? 'border-red-200' : 'border-amber-300'
  const body = tone === 'red' ? 'text-red-900' : 'text-amber-900'
  return (
    <div className="mt-4" data-testid={testId}>
      <p className={`text-xs font-semibold uppercase tracking-wide ${head}`}>
        {heading}
      </p>
      {blurb && <p className="mt-1 text-xs text-muted-foreground">{blurb}</p>}
      <ul className="mt-2 space-y-2">
        {items.map((item, i) => (
          <li key={i} className={`rounded border bg-white p-3 ${card}`}>
            <p className="text-sm italic text-gray-800">
              &ldquo;{item.requested_text}&rdquo;
            </p>
            {item.hits.map((h, j) => (
              <div key={j} className="mt-2">
                <p className={`text-sm ${body}`}>
                  <span className="font-mono text-xs">{h.assertion_id}</span>{' '}
                  {h.rationale}
                </p>
                {showAlternatives && h.safe_alternative && (
                  <p className="mt-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-sm text-emerald-900">
                    <span className="font-semibold">Instead: </span>
                    {h.safe_alternative}
                  </p>
                )}
              </div>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface ClassifyResultProps {
  decision: RoutingDecision
  policy: AttestPolicy | null
}

/**
 * Two testids on purpose. `classify-result` is the wrapper and exists whenever a
 * decision came back; `routing-decision` stays scoped to the routed case,
 * because the Playwright refusal spec asserts it is absent on a refusal —
 * refused must not look routed to a machine either.
 */
export function ClassifyResult({ decision, policy }: ClassifyResultProps) {
  const routed = !decision.refused
  return (
    <div
      data-testid="classify-result"
      className={`rounded-lg border p-4 ${
        routed ? 'border-emerald-200 bg-emerald-50/60' : 'border-red-200 bg-red-50/50'
      }`}
    >
      {routed ? (
        <div data-testid="routing-decision">
          <h3 className="text-sm font-semibold text-emerald-900">
            Routed to {policy?.engagement_types?.[decision.engagement_type ?? '']?.label
              ?? decision.engagement_type}
          </h3>
          <p className="mt-1 font-mono text-[11px] text-emerald-700">
            {decision.engagement_type} · policy {decision.policy_version}
          </p>
        </div>
      ) : (
        <div data-testid="classify-refusal-summary">
          <h3 className="text-sm font-semibold text-red-900">
            {REFUSAL_LABELS[decision.refusal_reason ?? '']
              ?? 'This request cannot be issued as a compliant letter'}
          </h3>
          <p className="mt-1 font-mono text-[11px] text-red-700">
            {decision.refusal_reason ?? 'REFUSED'} · policy {decision.policy_version}
          </p>
          {decision.refusal_detail && (
            <p className="mt-2 text-sm text-red-900" data-testid="classify-refusal-detail">
              {decision.refusal_detail}
            </p>
          )}
        </div>
      )}

      {decision.permissible_items.length > 0 && (
        <div className="mt-3" data-testid="permissible-items">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900/70">
            {routed
              ? 'Can be covered'
              : 'Would have been permissible — but the request was refused, so none of it can be issued'}
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm text-gray-800">
            {decision.permissible_items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}

      {/* Needs-review before excluded: a could-not-check item is the one the CPA
          can act on right now (retry, or read it themselves). */}
      <ItemBucket
        testId="review-items"
        tone="amber"
        heading="Needs CPA review — the compliance check could not run"
        blurb={
          'These were never cleared, so they are NOT in the letter. Nothing here '
          + 'is known to be prohibited either — it is unverified, which is not the '
          + 'same thing. Re-run the classification, or read each item yourself and '
          + 'confirm it is a plain fact from your own records.'
        }
        items={decision.review_items ?? []}
        showAlternatives={false}
      />

      <ItemBucket
        testId="excluded-items"
        tone="red"
        heading="Dropped from the ask — cannot be said"
        items={decision.excluded_items}
        showAlternatives
      />

      {decision.notes.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-xs text-gray-600">
          {decision.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </div>
  )
}
